import { GemMark } from "@/components/c360/GemMark";
import { useEffect, useRef, useState } from "react";
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { api } from "@/lib/api";
import ParticleHeading from "@/components/ParticleHeading";
import { radarSampler, chartSampler } from "@/components/particleShapes";
import { TrustedByBand } from "@/components/TrustedByBand";
import {
  ModuleCanvas,
  FinaleCanvas,
  TypedTerminal,
} from "@/components/HomepageCanvases";
import {
  ArrowRight, Search, ScanSearch, GitCompareArrows, Building2, Calculator,
  LineChart, Radar, Linkedin, Mail,
  Bell, LayoutGrid, Crosshair, ChevronDown, Target, ShieldAlert, Radio,
} from "lucide-react";
import { useBotDetectionAccess } from "@/lib/botDetection";
import { useLiveIntelAccess, liveCardBody } from "@/lib/liveIntel";
import { useCreator360Access } from "@/lib/creator360";
import { Gem } from "lucide-react";
import { AdminFooterLink } from "@/components/AdminFooterLink";

const stats = [
  { key: "total_kols", label: "KOLs tracked", suffix: "+", format: (n) => n.toLocaleString() },
  { key: "exchange_count", label: "Platforms mapped", suffix: "+", format: () => "20" },
];

const CORE_FEATURES = [
  {
    n: "01",
    icon: ScanSearch,
    tone: "var(--primary)",
    title: "Daily KOL Discovery",
    body: "Find and manage high-performing, under-the-radar traders and influencers across crypto, forex and equities — all in one unified place.",
    cta: "Open Creator Intel",
    to: "/search",
    testid: "feature-discovery",
    visual: "discovery",
    span: "small",
  },
  {
    n: "02",
    icon: LineChart,
    tone: "var(--primary)",
    title: "Creator Intel",
    body: "Track real-time, high-value metrics and performance data for every creator — subscribers, engagement, upload cadence and content bias.",
    cta: "Explore Creators",
    to: "/search",
    testid: "feature-creator-intel",
    visual: "sparkline",
    span: "wide",
  },
  {
    n: "03",
    icon: Building2,
    tone: "var(--accent)",
    title: "Exchange Intel",
    body: "Monitor the influencer marketing activities of competitors through real-time exchange mapping.",
    cta: "Open Exchange Intel",
    to: "/exchange-mapping",
    testid: "feature-exchange-intel",
    visual: "network",
    span: "wide",
  },
  {
    n: "04",
    icon: LayoutGrid,
    tone: "var(--accent)",
    title: "Gap & Exclusion Matrix",
    body: "See which creators promote a competitor exchange but not yours — or neither — and instantly surface your highest-value acquisition targets.",
    cta: "Open Gap Matrix",
    to: "/gap-matrix",
    testid: "feature-gap-matrix",
    visual: "matrix",
    span: "small",
  },
  {
    n: "05",
    icon: Calculator,
    tone: "var(--primary)",
    title: "ROI Calculator",
    body: "Evaluate the financial viability of every deal before onboarding a KOL, with instant suggestions for required adjustments if any deal falls short.",
    cta: "Model a Deal",
    to: "/roi-calculator",
    testid: "feature-roi",
    visual: "bars",
    span: "small",
  },
  {
    n: "06",
    icon: GitCompareArrows,
    tone: "var(--accent)",
    title: "Compare",
    body: "Benchmark multiple creators and competitor platforms side-by-side using key performance metrics — pick the right partner in seconds.",
    cta: "Compare Now",
    to: "/compare",
    testid: "feature-compare",
    visual: "compare",
    span: "small",
  },
  {
    n: "07",
    icon: Bell,
    tone: "var(--primary)",
    title: "Creator & Radar Alerts",
    body: "Radar Alerts watch every creator on the platform and ping you the moment any of them starts promoting an exchange, goes quiet on one, or returns after a long silence — narrowed by any conditions you set.",
    cta: "Open Alerts",
    to: "/alerts",
    testid: "feature-alerts",
    isNew: true,
    visual: "ping",
    span: "small",
  },
  {
    n: "08",
    icon: Crosshair,
    tone: "var(--primary)",
    title: "Prospect Radar",
    body: "Pick who you are, set your criteria, and see every creator who fits but hasn't promoted you yet — with a clear read on how much of that universe you've already covered and how much is still untapped.",
    cta: "Open Prospect Radar",
    to: "/prospect-radar",
    testid: "feature-prospect-radar",
    isNew: true,
    visual: "radar",
    span: "full",
  },
  {
    n: "09",
    icon: Target,
    tone: "var(--accent)",
    title: "My Radar",
    body: "Your private pipeline for every creator — track status, notes, deal details and insights in one place, visible only to your team.",
    cta: "Open My Radar",
    to: "/my-radar",
    testid: "feature-my-radar",
    visual: "pipeline",
    span: "full",
  },
];

// v63 — with Bot Detection on: Prospect Radar + My Radar share a row, Bot Detection full width below.
const BOT_DETECTION_FEATURE = {
  n: "10",
  icon: ShieldAlert,
  tone: "var(--primary)",
  title: "Bot Detection",
  body: "See every creator whose subscriber count looks inflated — with the likely real audience — and filter by the exchange they promote.",
  cta: "Open Bot Detection",
  to: "/bot-detection",
  testid: "feature-bot-detection",
  isNew: true,
  visual: "botflag",
  span: "full",
};
// v77 — extra cards (Bot Detection, Live Streaming Intel, Creator 360) pair up in rows; an odd last one runs full width.
const featuresFor = (botOn, live, c360) => {
  const extra = [
    ...(botOn ? [BOT_DETECTION_FEATURE] : []),
    // v65 — Live Streaming Intel: only for people with access.
    ...(live ? [{ ...LIVE_INTEL_FEATURE, body: liveCardBody(live) }] : []),
    ...(c360 ? [CREATOR360_FEATURE] : []),
  ];
  if (!extra.length) return CORE_FEATURES;
  const core = CORE_FEATURES.map((f) => (f.span === "full" ? { ...f, span: "half" } : f));
  return [...core, ...extra.map((f, i) => ({ ...f, n: String(10 + i), span: extra.length % 2 && i === extra.length - 1 ? "full" : "half" }))];
};
const MODULE_WORDS = { 9: "Nine", 10: "Ten", 11: "Eleven", 12: "Twelve" };

const CREATOR360_FEATURE = {
  icon: Gem,
  tone: "var(--accent)",
  title: "Creator 360",
  body: "Under-the-radar creators with real, engaged audiences, and where each one performs best: YouTube standard videos, YouTube live streams, Telegram or Instagram.",
  cta: "Open Creator 360",
  to: "/creator-360",
  testid: "feature-creator360",
  isNew: true,
  visual: "prism",
};

const LIVE_INTEL_FEATURE = {
  icon: Radio,
  tone: "var(--primary)",
  title: "Live Streaming Intel",
  cta: "Open Live Streaming Intel",
  to: "/live-streaming-intel",
  testid: "feature-live-intel",
  isNew: true,
  visual: "livepulse",
};

const SOCIAL_LINKS = [
  // Only real, kolradar-owned accounts here. Placeholder generic
  // profiles (twitter.com, youtube.com, telegram.org, github.com)
  // were removed until we have named handles — nothing is worse than
  // a footer icon that dead-ends on the platform homepage.
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/kolradar/" },
];

const FOOTER_COLUMNS = [
  {
    heading: "Product",
    links: [
      { label: "Creator Intel", to: "/search" },
      { label: "Exchange Intel", to: "/exchange-mapping" },
      { label: "Gap & Exclusion Matrix", to: "/gap-matrix" },
      { label: "Prospect Radar", to: "/prospect-radar" },
      { label: "Compare Creators", to: "/compare" },
      { label: "Compare Exchanges", to: "/exchange-compare" },
      { label: "ROI Calculator", to: "/roi-calculator" },
      { label: "Alerts", to: "/alerts" },
      { label: "My Radar", to: "/my-radar" },
    ],
  },
  {
    heading: "Markets",
    links: [
      { label: "Crypto Exchanges", to: "/exchange-mapping" },
      { label: "Forex Brokers", to: "/exchange-mapping" },
      { label: "Equities", to: "/exchange-mapping" },
    ],
  },
  {
    heading: "Studio",
    links: [
      // "About" used to link to an #about anchor that doesn't exist
      // as a section any more; the anchor is now the whole hero, so
      // linking there just returns the visitor to the top of the
      // page. Sending them to Home directly is more honest and works
      // even from other routes.
      { label: "About", to: "/" },
      { label: "Contact", to: "mailto:hello@kolradar.io" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Terms of Service", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Disclaimer", to: "/disclaimer" },
    ],
  },
];

/** Ambient CSS animations scoped to the landing page. Injected once. */
const LandingStyles = () => (
  <style>{`
    @keyframes kr-drift-a { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(60px,-40px) scale(1.06)} }
    @keyframes kr-drift-b { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-48px,44px) scale(1.08)} }
    @keyframes kr-drift-c { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(30px,60px) scale(1.05)} }
    @keyframes kr-shimmer { 0%{background-position:-140% 0} 100%{background-position:240% 0} }
    @keyframes kr-glow-pulse { 0%,100%{filter:drop-shadow(0 0 12px rgba(0,240,255,0.28))} 50%{filter:drop-shadow(0 0 24px rgba(0,240,255,0.6))} }
    @keyframes kr-grid-pan { 0%{background-position:0 0} 100%{background-position:80px 80px} }
    @keyframes kr-breathe { 0%,100%{opacity:1; transform:scale(1)} 50%{opacity:.85; transform:scale(1.012)} }
    @keyframes kr-icon-breathe { 0%,100%{box-shadow:0 0 0 0 rgba(0,240,255,0)} 50%{box-shadow:0 0 22px 0 rgba(0,240,255,0.28)} }
    @keyframes kr-eyebrow-breathe { 0%,100%{opacity:.7} 50%{opacity:1} }
    @keyframes kr-particle-float { 0%{transform:translate(0,0)} 25%{transform:translate(14px,-24px)} 50%{transform:translate(-8px,-40px)} 75%{transform:translate(-18px,-16px)} 100%{transform:translate(0,0)} }
    @keyframes kr-particle-pulse { 0%,100%{opacity:.15} 50%{opacity:.55} }
    @keyframes kr-cta-hue { 0%,100%{filter:hue-rotate(0deg)} 50%{filter:hue-rotate(20deg)} }
    /* Radar sweep — a thin cyan line drifts down the hero once every ~9s. */
    @keyframes kr-sweep {
      0%   { opacity: 0; transform: translateY(-100%); }
      6%   { opacity: 0.55; }
      55%  { opacity: 0.55; }
      70%  { opacity: 0; }
      100% { opacity: 0; transform: translateY(320%); }
    }
    /* Random "ping" pulse — a tiny cyan ring blooms then fades. */
    @keyframes kr-ping { 0% { transform: scale(0.4); opacity: 0.9; } 100% { transform: scale(3.2); opacity: 0; } }
    /* Icon micro-tilt on card hover, layered on top of the resting breathe. */
    @keyframes kr-icon-tilt { 0%,100%{transform:rotate(0deg)} 50%{transform:rotate(3deg)} }

    /* NEW-badge pulse + card glow — flags a recent feature.
       Both are easy to remove: delete isNew:true on the feature entry
       and the badge + halo disappear; the keyframes below are inert
       without the class. Kept intentionally subtle so it never fights
       for attention with primary CTAs. */
    @keyframes kr-new-badge-pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(0,240,255,0.0); }
      50%     { box-shadow: 0 0 0 4px rgba(0,240,255,0.18); }
    }
    @keyframes kr-card-new-halo {
      0%,100% { box-shadow: 0 0 0 1px rgba(0,240,255,0.28), 0 0 22px -8px rgba(0,240,255,0.35); }
      50%     { box-shadow: 0 0 0 1px rgba(0,240,255,0.55), 0 0 38px -6px rgba(0,240,255,0.55); }
    }
    .kr-new-badge { animation: kr-new-badge-pulse 3.6s ease-in-out infinite; }
    .kr-card-new  { animation: kr-card-new-halo 5s ease-in-out infinite; }

    /* Persistent viewport-fixed background stack ─ subtle, GPU-only, does
       NOT re-render on scroll (position: fixed). Composited on its own
       layer via translateZ(0) to stay smooth on laptops + phones. */
    .kr-bg-layer {
      position: fixed; inset: 0;
      pointer-events: none;
      z-index: 0;
      overflow: hidden;
      transform: translateZ(0);
      background: var(--bg);
    }
    .kr-bg-grid {
      position: absolute; inset: -40px;
      background-image:
        linear-gradient(rgba(0,240,255,0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0,240,255,0.05) 1px, transparent 1px);
      background-size: 80px 80px;
      opacity: 0.55;
      mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
      -webkit-mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
      animation: kr-grid-pan 40s linear infinite;
      will-change: background-position;
    }
    .kr-bg-blob { position: absolute; border-radius: 9999px; filter: blur(90px); pointer-events: none; will-change: transform; }
    /* When the scroll story is active on the first screen, we tone the
       ambient circles down via a body-level class toggled from Landing. */
    body.kr-story-on .kr-bg-blob { opacity: 0.06 !important; }
    .kr-bg-blob-a { top: -180px;  left: 18%;     width: 720px; height: 720px; opacity: 0.18; background: radial-gradient(circle, #00F0FF 0%, transparent 60%); animation: kr-drift-a 22s ease-in-out infinite; }
    .kr-bg-blob-b { bottom: -220px; right: -80px; width: 620px; height: 620px; opacity: 0.14; background: radial-gradient(circle, #39FF14 0%, transparent 60%); animation: kr-drift-b 26s ease-in-out infinite; }
    .kr-bg-blob-c { top:  38%;    left:  -160px; width: 520px; height: 520px; opacity: 0.11; background: radial-gradient(circle, #00F0FF 0%, transparent 60%); animation: kr-drift-c 30s ease-in-out infinite; }
    .kr-bg-vignette {
      position: absolute; inset: 0;
      background: radial-gradient(ellipse at center, transparent 40%, rgba(10,10,11,0.55) 100%);
      pointer-events: none;
    }
    /* Slow-floating pinpoint particles — pure transforms, GPU friendly */
    .kr-particle {
      position: absolute; width: 3px; height: 3px; border-radius: 9999px;
      background: rgba(0,240,255,0.7); box-shadow: 0 0 12px rgba(0,240,255,0.55);
      opacity: 0.3; will-change: transform, opacity;
      animation: kr-particle-float 18s ease-in-out infinite, kr-particle-pulse 6s ease-in-out infinite;
    }
    .kr-particle.p-green { background: rgba(57,255,20,0.7); box-shadow: 0 0 12px rgba(57,255,20,0.55); }

    /* Continuous "breathing" — ~4-6s cycles. Very small deltas so it never
       reads as an alert or animation — it just makes elements feel alive. */
    .kr-breathe        { animation: kr-breathe 5.5s ease-in-out infinite; will-change: transform, opacity; }
    .kr-breathe-slow   { animation: kr-breathe 8s   ease-in-out infinite; will-change: transform, opacity; }
    .kr-icon-breathe   { animation: kr-icon-breathe 4.5s ease-in-out infinite; }
    .kr-eyebrow-breathe{ animation: kr-eyebrow-breathe 4s ease-in-out infinite; }
    .kr-cta-hue        { animation: kr-cta-hue 8s ease-in-out infinite; }
    /* Radar sweep — pinned to the hero via z-1, animated once every ~9s */
    .kr-radar-sweep {
      position: absolute; left: 0; right: 0; top: 0;
      height: 2px; pointer-events: none;
      background: linear-gradient(90deg, transparent 0%, rgba(0,240,255,0.4) 20%, rgba(0,240,255,0.7) 50%, rgba(0,240,255,0.4) 80%, transparent 100%);
      box-shadow: 0 0 22px rgba(0,240,255,0.6);
      opacity: 0;
      animation: kr-sweep 9s ease-in-out infinite;
      animation-delay: 3s;
      will-change: transform, opacity;
    }
    .kr-ping {
      position: absolute; width: 8px; height: 8px; border-radius: 9999px;
      border: 1px solid rgba(0,240,255,0.55);
      animation: kr-ping 2.4s ease-out infinite;
      pointer-events: none;
    }
    /* Sonar pulse — a central emitter radiates concentric rings outward,
       each ring fading as it grows. Distinct from the linear radar sweep,
       drifting blobs, and pinpoint particles already on the page. Pure CSS
       transform/opacity so the GPU handles it — safe on mobile. */
    .kr-sonar {
      position: absolute; width: 20px; height: 20px; border-radius: 9999px;
      pointer-events: none; will-change: transform, opacity;
    }
    .kr-sonar::before, .kr-sonar::after {
      content: ""; position: absolute; inset: 0;
      border-radius: 9999px; border: 1px solid rgba(0,240,255,0.45);
      box-shadow: 0 0 20px rgba(0,240,255,0.35) inset;
      animation: kr-sonar-expand 14s cubic-bezier(.16,.68,.32,.99) infinite;
      opacity: 0;
    }
    .kr-sonar::after { animation: kr-sonar-expand 17s cubic-bezier(.16,.68,.32,.99) infinite; animation-delay: 5.5s; }
    /* Second sonar emitter starts 8.2s later so the two big pings never
       breathe in lockstep. Pseudo-elements can't inherit inline animation
       delays, so we scope the offset via a modifier class. */
    .kr-sonar.d-offset::before { animation-delay: 8.2s; }
    .kr-sonar.d-offset::after  { animation-delay: 13.7s; }
    .kr-sonar.s-green::before, .kr-sonar.s-green::after {
      border-color: rgba(57,255,20,0.35);
      box-shadow: 0 0 20px rgba(57,255,20,0.25) inset;
    }
    @keyframes kr-sonar-expand {
      0%   { transform: scale(0.4); opacity: 0.55; }
      70%  { transform: scale(11);  opacity: 0.05; }
      100% { transform: scale(14);  opacity: 0;    }
    }
    /* Scroll-reveal — sections fade + rise into view when they enter the
       viewport. IntersectionObserver toggles the kr-in-view class. Ambient
       scan line adds a slow horizontal telemetry sweep so the page feels
       alive without stealing focus. */
    .kr-reveal { opacity: 0; transform: translateY(24px); transition: opacity 800ms cubic-bezier(.2,.7,.2,1), transform 800ms cubic-bezier(.2,.7,.2,1); }
    .kr-reveal.kr-in-view { opacity: 1; transform: translateY(0); }
    /* content-visibility: auto lets the browser skip layout / paint /
       raster of any .kr-reveal section that is off-screen during
       scroll. contain-intrinsic-size gives a placeholder height so
       the scrollbar does not jump as sections come into view.
       Combined with the existing IntersectionObserver-based canvas
       pausing, this is the single largest scroll-lag win on the
       homepage — off-screen module cards no longer paint on every
       scroll frame. .kr-in-view drops back to visible once the
       section has been seen so we never re-hide content the user has
       already reached. */
    .kr-reveal:not(.kr-in-view) {
      content-visibility: auto;
      contain-intrinsic-size: 1px 600px;
    }
    .kr-scanline { position: absolute; left: 0; right: 0; height: 1px;
      background: linear-gradient(90deg, transparent 0%, rgba(0,240,255,0.35) 50%, transparent 100%);
      animation: kr-scan 22s linear infinite; pointer-events: none; opacity: 0.28; }
    @keyframes kr-scan { 0% { top: -2%; } 100% { top: 102%; } }
    /* Enhance card hover — the icon block already has a resting breathe, add
       a soft tilt for extra life on interaction */
    .kr-card:hover .kr-icon-breathe { animation: kr-icon-breathe 4.5s ease-in-out infinite, kr-icon-tilt 1.6s ease-in-out infinite; }

    .kr-title-glow { animation: kr-glow-pulse 4.5s ease-in-out infinite; }
    .kr-title-shimmer {
      background: linear-gradient(100deg, rgba(0,240,255,1) 0%, rgba(140,255,235,1) 45%, rgba(0,240,255,1) 60%, rgba(0,240,255,1) 100%);
      background-size: 220% 100%;
      -webkit-background-clip: text; background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: kr-shimmer 6s linear infinite;
    }
    /* Particle heading — real text stays in the DOM for SEO and screen
       readers. When the canvas overlay is mounted, we fade the letters
       to transparent so only the dots are visible. Falls back to the
       exact CSS heading above when the canvas is disabled (reduce
       motion, no-hover phones). */
    .kr-particle-heading.kr-particle-on .kr-ph-white { color: transparent; }
    .kr-particle-heading.kr-particle-on .kr-ph-cyan {
      /* The shimmer already uses -webkit-text-fill-color: transparent, so
         we drop its animation too — the canvas carries the visuals now. */
      -webkit-text-fill-color: transparent;
      background: none;
      animation: none;
    }
    .kr-particle-heading.kr-particle-on .kr-title-glow { animation: none; }
    @keyframes kr-ph-pulse {
      0%, 100% { filter: drop-shadow(0 0 6px rgba(0,240,255,0.45)) drop-shadow(0 0 14px rgba(0,240,255,0.18)); }
      50%      { filter: drop-shadow(0 0 10px rgba(0,240,255,0.6)) drop-shadow(0 0 22px rgba(0,240,255,0.3)); }
    }

    /* Scroll hint under the pinned heading — a small pulsing chevron
       that also skips the story on click. */
    .kr-scroll-hint {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 8px 16px;
    }
    .kr-scroll-hint-arrow {
      animation: kr-scroll-hint-bounce 1.8s ease-in-out infinite;
    }
    @keyframes kr-scroll-hint-bounce {
      0%, 100% { transform: translateY(0); opacity: 0.55; }
      50%      { transform: translateY(4px); opacity: 1; }
    }

    /* Payoff section — a thin accent line at the very top and a soft
       radial glow behind the CTAs, giving the "landing moment" its
       weight without adding an image. */
    .kr-payoff-accent {
      position: absolute;
      top: 0; left: 50%;
      transform: translateX(-50%);
      width: 240px; height: 1px;
      background: linear-gradient(90deg, transparent, rgba(0,240,255,0.55), transparent);
      box-shadow: 0 0 24px rgba(0,240,255,0.3);
    }
    .kr-hero-payoff {
      position: relative;
      /* No top border — the heartbeat divider above is the section
         boundary. Prior border-top of rgba(255,255,255,0.04) was
         reading as a faint white line under the divider. */
      background: radial-gradient(ellipse at 50% 20%, rgba(0,240,255,0.05) 0%, transparent 60%);
    }
    .kr-card { transition: transform .28s cubic-bezier(.2,.9,.25,1), border-color .28s ease, box-shadow .28s ease, background .28s ease; }
    .kr-card:hover {
      transform: translateY(-4px);
      border-color: rgba(0,240,255,0.42);
      box-shadow: 0 0 0 1px rgba(0,240,255,0.25), 0 22px 44px -22px rgba(0,240,255,0.35);
      background: rgba(0,240,255,0.03);
    }
    .kr-cta-primary { transition: transform .18s ease, box-shadow .18s ease; }
    .kr-cta-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 14px 32px -12px rgba(0,240,255,0.55);
    }
    .kr-cta-ghost { transition: transform .18s ease, border-color .18s ease, background .18s ease; }
    .kr-cta-ghost:hover { transform: translateY(-2px); border-color: rgba(255,255,255,0.32); background: rgba(255,255,255,0.03); }
    /* --- Console panel around the payoff section ------------------
       Prototype-matched entrance: brackets slide in from 14 px
       outside their corners with a soft overshoot 0.1 s after the
       section arrives; content fades up 10 px 0.35 s later. Runs
       once (.kr-in-view added and not removed). */
    .kr-console {
      position: relative;
      max-width: 1000px;
      margin: 0 auto;
      border-radius: 22px;
      background:
        radial-gradient(ellipse at 50% -10%, rgba(0,240,255,0.06) 0%, transparent 55%),
        radial-gradient(rgba(0,240,255,0.09) 1px, transparent 1px);
      background-size: auto, 18px 18px;
      background-position: center top, 0 0;
      border: 1px solid rgba(255,255,255,0.06);
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
      padding: clamp(48px, 6vw, 72px) clamp(20px, 4vw, 56px);
    }
    .kr-console::before,
    .kr-console::after,
    .kr-console > .kr-console-tr,
    .kr-console > .kr-console-br {
      content: "";
      position: absolute; width: 22px; height: 22px; pointer-events: none;
      border: 2px solid rgba(0,240,255,0.85);
      opacity: 0;
      /* Overshoot cubic-bezier so brackets ease in with a soft snap. */
      transition: opacity 600ms cubic-bezier(.2,1.4,.35,1), transform 600ms cubic-bezier(.2,1.4,.35,1);
      transition-delay: 100ms;
    }
    .kr-console::before {
      top: 10px; left: 10px;
      border-right: 0; border-bottom: 0; border-top-left-radius: 8px;
      transform: translate(-14px, -14px);
    }
    .kr-console::after  {
      top: 10px; right: 10px;
      border-left: 0; border-bottom: 0; border-top-right-radius: 8px;
      transform: translate(14px, -14px);
    }
    .kr-console > .kr-console-tr {
      bottom: 10px; left: 10px;
      border-right: 0; border-top: 0; border-bottom-left-radius: 8px;
      transform: translate(-14px, 14px);
    }
    .kr-console > .kr-console-br {
      bottom: 10px; right: 10px;
      border-left: 0; border-top: 0; border-bottom-right-radius: 8px;
      transform: translate(14px, 14px);
    }
    .kr-console.kr-in-view::before,
    .kr-console.kr-in-view::after,
    .kr-console.kr-in-view > .kr-console-tr,
    .kr-console.kr-in-view > .kr-console-br {
      opacity: 1; transform: translate(0, 0);
    }
    /* Console content fade-up — driven by .kr-console.kr-in-view. */
    .kr-console-body { opacity: 0; transform: translateY(10px); transition: opacity 700ms ease, transform 700ms ease; transition-delay: 350ms; }
    .kr-console.kr-in-view .kr-console-body { opacity: 1; transform: translateY(0); }
    /* Dotted lead-in line that drops from the story into the console.
       2 px vertical cyan dotted line, 110 px tall, whose dot pattern
       animates downward on a 1.4 s loop. */
    .kr-console-dropline {
      position: absolute; left: 50%; top: -110px;
      width: 2px; height: 110px; transform: translateX(-50%);
      background-image: repeating-linear-gradient(
        180deg,
        rgba(0,240,255,0.85) 0 3px,
        transparent 3px 8px
      );
      background-size: 2px 16px;
      background-repeat: repeat-y;
      opacity: 0;
      transition: opacity 500ms ease;
      transition-delay: 250ms;
      animation: kr-dropflow 1.4s linear infinite;
    }
    @keyframes kr-dropflow { from { background-position: 0 0; } to { background-position: 0 16px; } }
    .kr-console.kr-in-view .kr-console-dropline { opacity: 0.85; }
    /* Blinking block cursor used inside the finale terminal line. */
    @keyframes kr-caret-blink { 50% { opacity: 0; } }

    /* --- Word-by-word heading reveal.
       Each word gets a small delay via inline --kr-word-i; on
       .kr-in-view they fade to their normal colour with a subtle
       lift. Cheap: transitions only, no rAF. */
    .kr-word {
      display: inline-block;
      opacity: 0.18;
      transform: translateY(6px);
      transition: opacity 500ms ease, transform 500ms ease;
      transition-delay: calc(var(--kr-word-i, 0) * 90ms);
    }
    .kr-in-view .kr-word { opacity: 1; transform: translateY(0); }

    /* --- Card polish: mouse-follow spotlight + subtle boot-in.
       The spotlight is a radial-gradient positioned via CSS
       custom properties updated on mousemove — no state churn. */
    .kr-card2 {
      position: relative;
      background: rgba(18,18,20,0.55);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 12px;
      overflow: hidden;
      transition: transform .28s cubic-bezier(.2,.9,.25,1), border-color .28s ease, background .28s ease;
    }
    .kr-card2::before {
      content: "";
      position: absolute; inset: 0;
      pointer-events: none;
      background: radial-gradient(320px circle at var(--kr-mx, 50%) var(--kr-my, 30%),
        rgba(0,240,255,0.10) 0%, transparent 55%);
      opacity: 0;
      transition: opacity 240ms ease;
    }
    .kr-card2:hover {
      transform: translateY(-3px);
      border-color: rgba(0,240,255,0.4);
      background: rgba(0,240,255,0.03);
    }
    .kr-card2:hover::before { opacity: 1; }
    /* Card visual holder — canvas renders directly onto the card
       surface (transparent background, no black plate that read as
       a blob). Sized to fit; canvas uses full width & height, so
       nothing clips regardless of card span (Discovery 4, Creator
       Intel/Exchange Intel 8, Prospect Radar full/tall). */
    .kr-card-vis {
      position: relative;
      height: 96px;
      margin-top: 18px;
      border-radius: 10px;
      background: transparent;
      border: none;
      overflow: visible;
    }
    .kr-card-vis .cv-svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
    /* Per-card sizing — wide cards (Creator Intel, Exchange Intel)
       get a bit more vertical room; the Prospect Radar tall column
       fills the full inner height of its 2-col row. */
    .kr-card-vis-wide { height: 130px; }
    .kr-card-vis-tall { height: 100%; min-height: 220px; }
    /* Boot-up flash — a soft brightness pulse when the section enters view. */
    @keyframes kr-boot { 0%,90%,100% { filter: brightness(1); } 15% { filter: brightness(1.35); } }
    .kr-in-view .kr-card-vis { animation: kr-boot 900ms ease-out both; animation-delay: calc(var(--kr-i,0) * 120ms + 120ms); }
    /* Radar sweep rotation — smooth CSS-only rotate around the
       centre of the visual. Paused at rest, active on card hover. */
    @keyframes cv-sweep-rot { to { transform: rotate(360deg); } }
    .cv-sweep { animation: cv-sweep-rot 6s linear infinite; animation-play-state: paused; transform-box: fill-box; }
    .kr-card2:hover .cv-sweep { animation-play-state: running; }
    /* Concentric ring pulse — outward scale + fade. Runs at rest
       (subtle) and slightly stronger on hover. */
    @keyframes cv-pulse-ring {
      0%   { transform: scale(0.85); opacity: 0.65; }
      70%  { transform: scale(1.35); opacity: 0; }
      100% { transform: scale(1.35); opacity: 0; }
    }
    .cv-pulse-ring { transform-origin: center; transform-box: fill-box; animation: cv-pulse-ring 2.6s ease-out infinite; animation-play-state: paused; }
    .kr-in-view .cv-pulse-ring { animation-play-state: running; }
    /* Alerts ring wave — subtly wider than the pulse ring. */
    @keyframes cv-ring-wave { 0% { opacity: 0.9; transform: scale(0.6); } 100% { opacity: 0; transform: scale(1.15); } }
    .cv-ring { transform-origin: center; transform-box: fill-box; animation: cv-ring-wave 2.8s ease-out infinite; animation-play-state: paused; }
    .kr-in-view .cv-ring { animation-play-state: running; }
    /* Exchange Intel — dashed edge pulse. */
    @keyframes cv-edge-pulse {
      0%,100% { stroke-opacity: 0.35; }
      50%     { stroke-opacity: 0.85; }
    }
    .cv-edge { animation: cv-edge-pulse 2.4s ease-in-out infinite; animation-play-state: paused; }
    .kr-in-view .cv-edge { animation-play-state: running; }
    /* Small pulsing dot (ROI last-bar summit). */
    @keyframes cv-pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
    .cv-pulse-dot { animation: cv-pulse-dot 1.6s ease-in-out infinite; animation-play-state: paused; }
    .kr-in-view .cv-pulse-dot { animation-play-state: running; }
    /* Pause every visual animation when the whole card visual is
       off screen (visibility-based rather than section-based). */
    .kr-card-vis:not(:hover):not(.kr-in-view) .cv-sweep,
    .kr-card-vis:not(:hover):not(.kr-in-view) .cv-pulse-ring,
    .kr-card-vis:not(:hover):not(.kr-in-view) .cv-ring,
    .kr-card-vis:not(:hover):not(.kr-in-view) .cv-edge,
    .kr-card-vis:not(:hover):not(.kr-in-view) .cv-pulse-dot { animation-play-state: paused; }

    /* --- Market pulse strip: purely decorative heartbeat line that
       drifts left-to-right. Clean: no top/bottom border or gradient
       fill (both used to leave thin white fringes that made the
       strip look like a blob). Motion pauses when the strip is off
       screen via the .kr-pulse-paused class toggled by the shared
       IntersectionObserver. */
    .kr-pulse-strip {
      position: relative;
      height: 44px;
      overflow: hidden;
      background: transparent;
    }
    .kr-pulse-track {
      position: absolute; top: 50%; left: 0; height: 22px;
      transform: translateY(-50%);
      display: flex; align-items: center;
      animation: kr-pulse-drift 24s linear infinite;
      will-change: transform;
    }
    .kr-pulse-strip.kr-pulse-paused .kr-pulse-track { animation-play-state: paused; }
    .kr-pulse-track svg { display: block; }
    @keyframes kr-pulse-drift {
      0%   { transform: translate(0%, -50%); }
      100% { transform: translate(-50%, -50%); }
    }
    .kr-pulse-strip::after {
      /* Vignette so the wave fades at the edges. Uses var(--bg) so it
         matches whatever section wraps the strip. */
      content: ""; position: absolute; inset: 0; pointer-events: none;
      background: linear-gradient(90deg, var(--bg) 0%, transparent 10%, transparent 90%, var(--bg) 100%);
    }

    /* --- Finale (Enter Terminal) — ripple pings behind the button. */
    .kr-finale {
      position: relative;
      display: inline-flex; align-items: center; justify-content: center;
      padding: 40px 0;
    }
    .kr-finale-ping {
      position: absolute; left: 50%; top: 50%;
      width: 60px; height: 60px; margin: -30px 0 0 -30px;
      border-radius: 50%;
      border: 1px solid rgba(0,240,255,0.5);
      opacity: 0;
      animation: kr-finale-ping 3.6s ease-out infinite;
    }
    .kr-finale-ping.d2 { animation-delay: 1.2s; }
    .kr-finale-ping.d3 { animation-delay: 2.4s; }
    .kr-finale:hover .kr-finale-ping { animation-duration: 1.6s; }
    @keyframes kr-finale-ping {
      0%   { transform: scale(.5); opacity: .8; }
      100% { transform: scale(4.8); opacity: 0; }
    }
    /* Terminal-line typing effect (optional) */
    .kr-term-typing {
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      border-right: 2px solid rgba(0,240,255,0.6);
      max-width: 0;
      animation: kr-term-type 1400ms steps(14, end) forwards, kr-term-caret .8s step-end infinite;
    }
    .kr-in-view .kr-term-typing { animation-play-state: running; }
    @keyframes kr-term-type { to { max-width: 220px; } }
    @keyframes kr-term-caret { 50% { border-color: transparent; } }

    /* --- Perf recovery: kill the heavy 90px blur when the story is
       on-screen (the ambient blobs still show through as gentle
       colour tints via opacity, without the GPU cost). */
    body.kr-story-on .kr-bg-blob { filter: none !important; }
    body.kr-story-on .kr-bg-grid { opacity: 0.28 !important; }

    @media (prefers-reduced-motion: reduce) {
      .kr-bg-blob, .kr-bg-grid, .kr-title-glow, .kr-title-shimmer,
      .kr-particle, .kr-breathe, .kr-breathe-slow, .kr-icon-breathe,
      .kr-eyebrow-breathe, .kr-cta-hue, .kr-radar-sweep, .kr-ping, .kr-sonar,
      .kr-new-badge, .kr-card-new, .kr-ph-canvas,
      .kr-finale-ping, .kr-term-typing { animation: none !important; }
      .kr-sonar::before, .kr-sonar::after { animation: none !important; opacity: 0.15; }
      .kr-card:hover, .kr-card2:hover { transform: none; }
      .kr-word { opacity: 1; transform: none; transition: none; }
    }
  `}</style>
);

/** Persistent viewport-fixed ambient background — drifting cyan/green blobs
 * plus a faint animated grid. All CSS keyframe transforms (GPU-composited),
 * `position: fixed` keeps it glued to the viewport so scrolling never
 * triggers a repaint of this layer. */
const AmbientBackground = () => (
  <div className="kr-bg-layer" aria-hidden="true">
    <div className="kr-bg-grid" />
    <div className="kr-bg-blob kr-bg-blob-a" />
    <div className="kr-bg-blob kr-bg-blob-b" />
    {/* Sonar emitter — a single centre-left ring source ping. Second
        emitter and 12-particle field cut for perf — story-on class
        keeps the ambient light, at ~40 % of the previous GPU cost. */}
    <div className="kr-sonar" style={{ top: "32%", left: "16%" }} />
    <div className="kr-scanline" style={{ top: "12%" }} />
    <div className="kr-bg-vignette" />
  </div>
);

/** Count-up hook — animates a number from 0 → target on mount, respects
 * prefers-reduced-motion. Kept small on purpose (no external lib). */
function useCountUp(target, { duration = 1400, run = true } = {}) {
  const [value, setValue] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!run || !target) { setValue(target || 0); return; }
    if (reduce) { setValue(target); return; }
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const step = (t) => {
      const elapsed = t - start;
      const p = Math.min(1, elapsed / duration);
      // ease-out-quart
      const eased = 1 - Math.pow(1 - p, 4);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, run, reduce]);
  return value;
}

/**
 * Scroll story runway — returns `progress` (0..1) representing how far
 * the caller's `containerRef` has scrolled through its own pin range.
 *
 * Cheap: one passive scroll listener, rAF-batched. The ref's rect is
 * read every rAF while the container is in the viewport; when it
 * scrolls out we stop reading. On mobile / reduced motion / no-hover
 * devices `enabled` is false and this hook does nothing (no listener,
 * no rAF, no state updates) — the plain hero renders as a normal
 * section.
 */
function useScrollStoryProgress(containerRef, enabled) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;
    let rafId = 0;
    let inView = false;
    // Skip trivial-delta state updates so scrolling through the
    // pinned story doesn't re-render the entire Landing tree at
    // 60 Hz. 0.004 ≈ 4 frames per full morph band — visually smooth,
    // ~1/4 the React work of unthrottled updates.
    let lastPushed = -1;
    const _read = () => {
      rafId = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // pin range = container height minus one viewport (the sticky
      // inner sits atop the last vh of the container).
      const range = rect.height - vh;
      if (range <= 0) {
        if (lastPushed !== 0) { lastPushed = 0; setProgress(0); }
        return;
      }
      // Progress starts when the container's top reaches the top of
      // the viewport and finishes when its bottom leaves the bottom.
      const scrolled = Math.max(0, -rect.top);
      const next = Math.max(0, Math.min(1, scrolled / range));
      // Always let the endpoints (0 / 1) through so any state that
      // depends on "story fully at rest" or "story fully done" is
      // authoritative regardless of how the user landed there.
      if (
        Math.abs(next - lastPushed) > 0.004 ||
        (next === 0 && lastPushed !== 0) ||
        (next === 1 && lastPushed !== 1)
      ) {
        lastPushed = next;
        setProgress(next);
      }
    };
    const _schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(_read);
    };
    const io = new IntersectionObserver((entries) => {
      inView = entries[0] && entries[0].isIntersecting;
      if (inView) _schedule();
    }, { threshold: 0 });
    io.observe(el);
    const _onScroll = () => { if (inView) _schedule(); };
    window.addEventListener("scroll", _onScroll, { passive: true });
    window.addEventListener("resize", _schedule, { passive: true });
    _schedule();
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", _onScroll);
      window.removeEventListener("resize", _schedule);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [containerRef, enabled]);
  return progress;
}

/** v36 — subtle vertical dotted divider used between homepage sections.
 *  Visually mirrors the thin dotted drop-line that already sits above
 *  the "Enter the terminal" console (`.kr-console-dropline`), but
 *  without the downward dot-flow animation. Static, cheap, and
 *  deliberately quiet — just enough to say "new section" without
 *  stealing attention like the old heartbeat line did. */
function DottedDivider({ testid, extraStyle }) {
  return (
    <div
      className="relative z-10 flex items-center justify-center"
      data-testid={testid}
      aria-hidden="true"
      style={extraStyle}
    >
      <span
        style={{
          display: "inline-block",
          width: "2px",
          height: "84px",
          backgroundImage: "repeating-linear-gradient(180deg, rgba(0,240,255,0.55) 0 3px, transparent 3px 8px)",
          backgroundSize: "2px 16px",
          backgroundRepeat: "repeat-y",
          opacity: 0.75,
        }}
      />
    </div>
  );
}

/** Split a heading into words wrapped in `.kr-word` spans with a
 *  staggered fade-in delay driven by `--kr-word-i`. Relies on the
 *  parent carrying `.kr-reveal` + `.kr-in-view` (already wired via
 *  the shared IntersectionObserver in `Landing`). */
function WordHeading({ text, colored = null, className = "" }) {
  const words = text.split(" ");
  return (
    <span className={className}>
      {words.map((w, i) => {
        const isColored = colored && colored.includes(w);
        return (
          <React.Fragment key={i}>
            <span className="kr-word" style={{ "--kr-word-i": i, color: isColored ? "var(--primary)" : undefined }}>{w}</span>
            {i < words.length - 1 ? " " : ""}
          </React.Fragment>
        );
      })}
    </span>
  );
}

function AnimatedStat({ statConfig, live }) {
  // Count up once, when the element scrolls into view. Guardrail:
  // never render "0" as a KOL/exchange counter — that reads as
  // broken during a live pitch. If we somehow get a 0 (state-reload
  // race, API failure with no cache) suppress the number entirely.
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) { setVisible(true); io.disconnect(); }
    }, { threshold: 0.4 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const isZero = !live || live === 0;
  const target = isZero ? 0 : live;
  const displayed = useCountUp(target, { duration: 1400, run: visible && !reduce });
  return (
    <div ref={ref}>
      <div
        className="display text-2xl sm:text-3xl font-semibold kr-breathe-slow"
        style={{ color: "var(--primary)" }}
        data-testid={`stat-${statConfig.key}`}
      >
        {isZero ? "—" : `${statConfig.format(reduce ? target : displayed)}${statConfig.suffix || ""}`}
      </div>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{statConfig.label}</div>
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const botOn = useBotDetectionAccess().enabled;
  const liveFeatures = useLiveIntelAccess().features;
  const c360On = useCreator360Access().enabled;
  const features = featuresFor(botOn, liveFeatures, c360On);
  const [q, setQ] = useState("");
  // Hydrate KOLs-tracked counter from localStorage so it never flashes "0+"
  // during a state reload. Falls back to a safe non-zero baseline for first-
  // time visitors so the hero always displays a credible number.
  const HERO_COUNTS_KEY = "kolradar:hero-counts-v1";
  const readCachedCounts = () => {
    try {
      const raw = localStorage.getItem(HERO_COUNTS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.total_kols === "number" && parsed.total_kols > 0) return parsed;
    } catch {}
    return null;
  };
  const _cached = readCachedCounts();
  // Baseline (updated only when the API disagrees): first-time visitors see
  // a credible number instead of "0+" while /stats/public is in flight.
  const HERO_BASELINE_KOLS = 1000;
  const [counts, setCounts] = useState(_cached || { total_kols: HERO_BASELINE_KOLS, exchange_count: 20 });
  const [allExchanges, setAllExchanges] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [hi, setHi] = useState(-1);
  const suggestBoxRef = useRef(null);
  const reduce = useReducedMotion();

  // Scroll-story pin — desktop only. Uses matchMedia so we don't do
  // any work on phones/tablets, small laptops in portrait, or when the
  // user has asked for reduced motion. Guardrail: the plain hero
  // renders when `hasScrollStory` is false, so no layout jump, ever.
  const [hasScrollStory, setHasScrollStory] = useState(false);
  useEffect(() => {
    if (reduce) return;
    const mq = window.matchMedia("(min-width: 1024px) and (hover: hover) and (pointer: fine)");
    const _apply = () => {
      setHasScrollStory(mq.matches);
      // Toggle body class so CSS can tone down the ambient blobs
      // without a slow `:has()` selector.
      if (typeof document !== "undefined") {
        document.body.classList.toggle("kr-story-on", mq.matches);
      }
    };
    _apply();
    mq.addEventListener?.("change", _apply);
    return () => {
      mq.removeEventListener?.("change", _apply);
      if (typeof document !== "undefined") document.body.classList.remove("kr-story-on");
    };
  }, [reduce]);
  const storyRef = useRef(null);
  const payoffRef = useRef(null);
  // Cards boot-up sequence — set a timestamp once the grid becomes
  // 20 % visible; each card canvas derives its own delayed run
  // window from `bootStartAt + 140ms * cardIdx`.
  const cardsSectionRef = useRef(null);
  const [bootStartAt, setBootStartAt] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") { setBootStartAt(performance.now()); return; }
    const el = cardsSectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setBootStartAt(performance.now());
        io.disconnect();
      }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // Finale button ref — powers the ring emission origin.
  const enterTerminalRef = useRef(null);
  const finaleSectionRef = useRef(null);
  const storyProgress = useScrollStoryProgress(storyRef, hasScrollStory);

  // Bind heading morph to `storyProgress`. Because the payoff section
  // lives OUTSIDE the pinned area now, we no longer need to fade any
  // sub-content — the runway is dedicated to the story alone. Bands:
  //  • 0.00 – 0.20 : heading at rest (scroll hint visible, fading)
  //  • 0.20 – 0.45 : morph heading → radar
  //  • 0.45 – 0.60 : radar holds
  //  • 0.60 – 0.85 : morph radar → chart
  //  • 0.85 – 1.00 : chart holds
  const _clamp01 = (v) => Math.max(0, Math.min(1, v));
  let morphProgress = 0;
  if (storyProgress < 0.20) {
    morphProgress = 0;
  } else if (storyProgress < 0.45) {
    morphProgress = ((storyProgress - 0.20) / 0.25) * 0.5;
  } else if (storyProgress < 0.60) {
    morphProgress = 0.5;
  } else if (storyProgress < 0.85) {
    morphProgress = 0.5 + ((storyProgress - 0.60) / 0.25) * 0.5;
  } else {
    morphProgress = 1;
  }
  const shapeSamplers = React.useMemo(
    () => (hasScrollStory ? [radarSampler, chartSampler] : null),
    [hasScrollStory]
  );
  const captionText = morphProgress < 0.15
    ? ""
    : morphProgress < 0.75
      ? "Discover and monitor every creator"
      : "Win the right deals";
  const captionOpacity = _clamp01(
    morphProgress < 0.15 ? 0
    : morphProgress < 0.40 ? (morphProgress - 0.15) / 0.25
    : morphProgress < 0.55 ? 1
    : morphProgress < 0.70 ? 1 - (morphProgress - 0.55) / 0.15 * 0.4
    : morphProgress < 0.95 ? _clamp01(0.6 + (morphProgress - 0.70) / 0.25 * 0.4)
    : 1
  );
  // Eyebrow ("KOL Discovery on Steroids") fades out as soon as the
  // story starts moving past the heading — it must not linger over
  // the radar / chart stages. Fully hidden by the time morph starts.
  const eyebrowOpacity = _clamp01(
    storyProgress < 0.08 ? 1
    : storyProgress < 0.20 ? 1 - (storyProgress - 0.08) / 0.12
    : 0
  );
  // Fade the shape canvas out at the very tail of the story so the
  // growth chart doesn't slice under the sticky top nav while the
  // next section scrolls in. Kept opaque through 0.92 so the chart
  // is fully readable at rest.
  const canvasFadeOpacity = _clamp01(
    storyProgress < 0.92 ? 1
    : storyProgress < 1.0 ? 1 - (storyProgress - 0.92) / 0.08
    : 0
  );

  useEffect(() => {
    api.get("/stats/public").then((r) => {
      if (r?.data?.total_kols && r.data.total_kols > 0) {
        setCounts(r.data);
        try { localStorage.setItem(HERO_COUNTS_KEY, JSON.stringify(r.data)); } catch {}
      }
    }).catch(() => {});
    api.get("/kols/platforms").then((r) => {
      const all = [];
      (r.data.categories || []).forEach((c) => (c.platforms || []).forEach((p) => all.push(p)));
      setAllExchanges(all);
    }).catch(() => {});
  }, []);

  // Scroll-reveal — add `.kr-in-view` to `.kr-reveal` sections as they hit
  // the viewport. Runs once per section, gracefully no-ops if IO unsupported.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("kr-in-view");
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: "0px 0px -12% 0px", threshold: 0.08 });
    document.querySelectorAll(".kr-reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Debounced autocomplete: on input, ask the backend for up to 6 name-matching
  // KOLs across the FULL dataset AND surface any exchange whose name starts
  // with the query. Anonymous visitors just get exchange matches (KOL fetch
  // 401s silently — the axios interceptor treats it as a generic auth probe).
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setSuggestions([]);
      setShowSug(false);
      setHi(-1);
      return;
    }
    const t = setTimeout(() => {
      const lower = query.toLowerCase();
      const exMatches = allExchanges
        .filter((ex) => ex.toLowerCase().includes(lower))
        .slice(0, 3)
        .map((name) => ({ type: "exchange", label: name, sub: "Exchange", to: `/exchange/${encodeURIComponent(name)}` }));
      // v35: fan out to promoters + both unaffiliated lists so the
      // homepage search bar can jump to any creator on the site.
      // Each promoter result opens `/kol/{id}`; each unaffiliated
      // result opens `/unaffiliated/{id}` (the right profile page).
      // Admin-hidden creators and expired graduates are excluded by
      // the backend. When either unaffiliated flag is off the
      // corresponding fetch is a no-op (promise rejects or 404 →
      // empty list), so the live site keeps today's exact results.
      const promoterP = api.get("/kols", { params: { q: query, limit: 6, sort: "subscriber_count", order: "desc" } })
        .then((r) => (r.data.items || []).map((k) => ({
          type: "kol",
          id: k.id,
          label: k.channel_name,
          sub: `${Number(k.subscriber_count || 0).toLocaleString()} subs · ${(k.primary_exchange || k.exchanges?.[0] || "—")}`,
          to: `/kol/${k.id}`,
          raw: k,
        })))
        .catch(() => []);
      const unaffP = api.get("/unaffiliated/creators", { params: { q: query, page_size: 6, list: "all", sort: "subscribers", order: "desc" } })
        .then((r) => (r.data.results || []).map((k) => ({
          type: "unaffiliated",
          id: k.id,
          label: k.channel_name,
          sub: `${Number(k.subscriber_count || 0).toLocaleString()} subs · Unaffiliated${k.list_slug === "crypto" ? " · Crypto" : ""}`,
          to: `/unaffiliated/${k.id}`,
        })))
        .catch(() => []);
      Promise.all([promoterP, unaffP]).then(([promoter, unaff]) => {
        // Interleave: promoter hits first, then unaffiliated so the
        // familiar order on the live site stays unchanged for users
        // typing an exchange or a promoter.
        setSuggestions([...exMatches, ...promoter, ...unaff]);
        setShowSug(exMatches.length + promoter.length + unaff.length > 0);
        setHi(-1);
      }).catch(() => {
        setSuggestions(exMatches);
        setShowSug(exMatches.length > 0);
        setHi(-1);
      });
    }, 220);
    return () => clearTimeout(t);
  }, [q, allExchanges]);

  // Click-away to close the dropdown
  useEffect(() => {
    const onDoc = (e) => {
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Seed sessionStorage cache with the KOL row from a suggestion click so
  // /kol/:id hydrates instantly on navigation (no "loading creator" blank
  // state). If it's an exchange suggestion, nothing to seed — nav is fast.
  const seedFromSuggestion = (s) => {
    if (s?.type === "kol" && s.raw?.id) {
      try {
        sessionStorage.setItem(`kolradar:kol:${s.raw.id}`, JSON.stringify(s.raw));
      } catch {}
    }
  };

  const goSearch = (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    // If a dropdown suggestion is highlighted, honour it on Enter
    if (hi >= 0 && suggestions[hi]) {
      seedFromSuggestion(suggestions[hi]);
      nav(suggestions[hi].to);
      return;
    }
    if (query) {
      const lower = query.toLowerCase();
      const exact = allExchanges.find((ex) => ex.toLowerCase() === lower);
      let hit = exact || null;
      if (!hit && lower.length >= 4) {
        const starts = allExchanges.filter((ex) => ex.toLowerCase().startsWith(lower));
        if (starts.length === 1) hit = starts[0];
      }
      if (hit) {
        nav(`/exchange/${encodeURIComponent(hit)}`);
        return;
      }
    }
    const params = query ? `?q=${encodeURIComponent(query)}` : "";
    nav(`/search${params}`);
  };

  // Common entrance animation — staggered fade + upward slide.
  const rise = {
    hidden: { opacity: 0, y: 16 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, delay: reduce ? 0 : i * 0.08, ease: [0.2, 0.9, 0.25, 1] },
    }),
  };

  return (
    <div className="relative">
      <LandingStyles />
      <AmbientBackground />

      {/* ============================================================
          HERO — TWO-PART LAYOUT

          Section A ("story stage"): the eyebrow and the dot heading
          own the entire first screen, centred. When `hasScrollStory`
          is on, the section carries a 300vh scroll runway and its
          inner container is sticky-pinned. The dots morph into the
          radar (~0.30-0.55 progress), hold, then into the growth
          chart (~0.70-0.95). A small scroll hint at the bottom of the
          pinned screen is clickable — clicking it skips straight to
          the payoff section below, so a regular visitor is never
          stuck scrolling to reach search.

          Section B ("payoff"): the subtitle / buttons / search /
          counters live in their OWN section immediately after the
          story. It's designed as the deliberate landing moment — a
          top accent line, centred content, a subtle radial glow
          behind the buttons for warmth, and framer-motion reveal on
          scroll-in so it feels like a payoff, not leftover content.

          When `hasScrollStory` is off (mobile, reduced motion), the
          story section collapses to natural height and both sections
          render as normal stacked content — the eyebrow / heading /
          payoff read as one continuous hero, exactly as before.
          ============================================================ */}
      <section
        id="about"
        ref={storyRef}
        className={`relative z-10 ${hasScrollStory ? "" : "overflow-hidden"}`}
        style={hasScrollStory ? { height: "300vh" } : undefined}
        data-testid="hero-story-section"
      >
        <div className="kr-radar-sweep" aria-hidden="true" />
        <div
          className="relative"
          style={hasScrollStory
            ? {
                position: "sticky",
                top: 0,
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "72px 24px 96px",
              }
            : { padding: "96px 24px 40px" }
          }
        >
          <div
            style={{
              opacity: hasScrollStory ? eyebrowOpacity : 1,
              transition: hasScrollStory ? "opacity 180ms linear" : undefined,
            }}
          >
            <motion.div
              variants={rise} initial="hidden" animate="show" custom={0}
              className={hasScrollStory
                ? "mono text-sm sm:text-base uppercase tracking-[0.32em] kr-eyebrow-breathe text-center"
                : "mono text-[11px] uppercase tracking-[0.25em] kr-eyebrow-breathe"
              }
              style={{
                color: "var(--accent)",
                textAlign: hasScrollStory ? "center" : "left",
              }}
            >
              KOL Discovery on Steroids
            </motion.div>
          </div>

          <motion.h1
            variants={rise} initial="hidden" animate="show" custom={1}
            className={hasScrollStory
              ? "display font-semibold leading-[1.02] mt-8 text-center text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] xl:text-[8.5rem]"
              : "display text-4xl sm:text-5xl lg:text-7xl leading-[1.05] font-semibold max-w-5xl mt-4"
            }
            style={hasScrollStory ? { letterSpacing: "-0.02em" } : undefined}
          >
            <ParticleHeading
              breakBefore
              shapeSamplers={shapeSamplers}
              progress={morphProgress}
              storyMode={hasScrollStory}
              canvasOpacity={hasScrollStory ? canvasFadeOpacity : 1}
            />
          </motion.h1>

          {hasScrollStory && (
            <div
              aria-hidden="true"
              data-testid="hero-story-caption"
              className="mono text-sm sm:text-base uppercase tracking-[0.3em] transition-opacity duration-200"
              style={{
                position: "absolute",
                // Sit ABOVE the shape area (which centres on the
                // viewport). At 900vh: shape top ~29vh; caption top
                // at 20vh keeps ~9vh clear space above the shape and
                // never crosses it. Also above the eyebrow's flex
                // position, so once the eyebrow fades the caption
                // fills the same "top label" role.
                top: "20vh",
                left: 0,
                right: 0,
                textAlign: "center",
                opacity: captionOpacity * canvasFadeOpacity,
                minHeight: "1.5em",
                color: "var(--accent)",
                pointerEvents: "none",
              }}
            >
              {captionText}
            </div>
          )}

          {/* Scroll hint — anchored to the bottom of the pinned screen.
              Clicking it skips straight to the payoff (search + CTAs). */}
          {hasScrollStory && (
            <button
              type="button"
              data-testid="hero-scroll-hint"
              aria-label="Skip to search"
              onClick={() => {
                const el = payoffRef.current;
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="kr-scroll-hint group"
              style={{
                position: "absolute",
                bottom: "32px",
                left: "50%",
                transform: "translateX(-50%)",
                opacity: Math.max(0, 1 - storyProgress * 4),
                pointerEvents: storyProgress > 0.25 ? "none" : "auto",
                transition: "opacity 200ms linear",
              }}
            >
              <span className="mono text-[10px] uppercase tracking-[0.35em] text-zinc-500 group-hover:text-white transition-colors">
                Scroll or skip
              </span>
              <ChevronDown size={16} className="mx-auto mt-2 text-zinc-500 group-hover:text-white transition-colors kr-scroll-hint-arrow" />
            </button>
          )}
        </div>
      </section>

      {/* Story → console: no divider. The only connector is the
          dotted drop-line rendered inside the console frame itself
          (see .kr-console-dropline). Matches the prototype. */}

      {/* ============================================================
          HERO PAYOFF — the deliberate landing moment after the story.
          Wrapped in a "console" frame: radar corner brackets + dot-
          grid texture + a dotted lead-in line drawing down from the
          story above. IntersectionObserver adds `.kr-in-view` so the
          brackets animate in only when the section actually appears.
          ============================================================ */}
      <section
        ref={payoffRef}
        className="relative z-10 kr-hero-payoff"
        data-testid="hero-payoff-section"
      >
        <div className="relative max-w-[1200px] mx-auto px-6 pt-24 pb-28">
          <div className="kr-console kr-reveal" data-testid="payoff-console">
            {/* Corner brackets — the two extra spans balance the ::before / ::after pair. */}
            <span className="kr-console-tr" aria-hidden="true" />
            <span className="kr-console-br" aria-hidden="true" />
            {hasScrollStory && <span className="kr-console-dropline" aria-hidden="true" />}
            <div className="kr-console-body">
            {hasScrollStory && (
              <div
                className="mono text-[10px] uppercase tracking-[0.35em] text-center"
                style={{ color: "var(--primary)" }}
              >
                Enter the terminal
              </div>
            )}

            <p
              className={hasScrollStory
                ? "mt-6 text-2xl sm:text-3xl md:text-4xl text-white text-center leading-[1.15] font-semibold max-w-3xl mx-auto"
                : "mt-8 text-xl text-zinc-300 max-w-3xl leading-snug font-medium"
              }
            >
              An intelligence engine built exclusively for trading platforms to dominate influencer marketing.
            </p>
            <p
              className={hasScrollStory
                ? "mt-5 text-base sm:text-lg text-zinc-400 text-center max-w-2xl mx-auto leading-relaxed"
                : "mt-4 text-base text-zinc-400 max-w-3xl leading-relaxed"
              }
            >
              Discover, monitor & onboard under-the-radar KOLs & Traders across Crypto, Forex, Commodities & Equity Markets.
            </p>

            <div
              className={hasScrollStory
                ? "mt-10 flex flex-wrap gap-4 justify-center"
                : "mt-10 flex flex-wrap gap-4"
              }
            >
              <button
                data-testid="hero-cta-search"
                onClick={() => nav("/search")}
                className="kr-cta-primary group inline-flex items-center gap-2 px-6 py-3 rounded-md font-medium text-[15px]"
                style={{ background: "var(--primary)", color: "#0A0A0B" }}
              >
                Discover Creators
                <ArrowRight size={16} className="transition-transform duration-150 group-hover:translate-x-0.5" />
              </button>
              <Link
                to="/exchange-mapping"
                data-testid="hero-cta-mapping"
                className="kr-cta-ghost inline-flex items-center gap-2 px-6 py-3 rounded-md font-medium text-[15px] border hairline"
              >
                <Building2 size={16} /> Exchange Intel
              </Link>
              <button
                data-testid="hero-cta-roi"
                onClick={() => nav("/roi-calculator")}
                className="kr-cta-ghost inline-flex items-center gap-2 px-6 py-3 rounded-md font-medium text-[15px] border hairline"
              >
                <Calculator size={16} /> Model a Deal
              </button>
            </div>

            {/* Quick search */}
            <form
              onSubmit={goSearch}
              className={hasScrollStory ? "mt-14 max-w-2xl mx-auto" : "mt-14 max-w-2xl"}
            >
              <div className={`mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3 ${hasScrollStory ? "text-center" : ""}`}>Quick Search</div>
              <div ref={suggestBoxRef} className="relative">
                <div className="flex items-center border hairline rounded-md overflow-hidden" style={{ background: "var(--surface)" }}>
                  <Search size={16} className="ml-4 text-zinc-500" />
                  <input
                    data-testid="quick-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => suggestions.length && setShowSug(true)}
                    onKeyDown={(e) => {
                      if (!showSug || !suggestions.length) return;
                      if (e.key === "ArrowDown") { e.preventDefault(); setHi((i) => Math.min(i + 1, suggestions.length - 1)); }
                      else if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
                      else if (e.key === "Escape") { setShowSug(false); setHi(-1); }
                    }}
                    placeholder="Search channel name or exchange…"
                    className="flex-1 bg-transparent px-3 py-3.5 outline-none text-[15px] placeholder:text-zinc-600"
                    autoComplete="off"
                  />
                  <button
                    data-testid="quick-search-submit"
                    type="submit"
                    className="px-5 py-3.5 mono text-xs uppercase tracking-[0.15em] transition-colors duration-150"
                    style={{ background: "var(--primary)", color: "#0A0A0B" }}
                  >
                    Search
                  </button>
                </div>
                {showSug && suggestions.length > 0 && (
                  <div
                    data-testid="quick-search-suggestions"
                    className="absolute top-full left-0 right-0 mt-2 border hairline rounded-md overflow-hidden z-30 shadow-2xl"
                    style={{ background: "var(--surface)" }}
                  >
                    {suggestions.map((s, i) => (
                      <Link
                        key={`${s.type}-${s.label}`}
                        to={s.to}
                        onMouseEnter={() => setHi(i)}
                        onClick={() => { seedFromSuggestion(s); setShowSug(false); }}
                        data-testid={`quick-search-suggestion-${i}`}
                        className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 transition-colors duration-100 ${i === hi ? "bg-white/5" : "hover:bg-white/5"}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {s.type === "exchange" ? <Building2 size={14} className="text-zinc-500 shrink-0" /> : <Radar size={14} style={{ color: "var(--primary)" }} className="shrink-0" />}
                          <div className="min-w-0">
                            <div className="text-sm text-white truncate flex items-center gap-1.5"><span className="truncate" data-blur-id={s.id || undefined}>{s.label}</span>{s.id && <GemMark id={s.id} testid={`quick-search-gem-${s.id}`} />}</div>
                            <div className="mono text-[10px] text-zinc-500 truncate">{s.sub}</div>
                          </div>
                        </div>
                        <ArrowRight size={12} className="text-zinc-500 shrink-0" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </form>

            {/* Stats strip — counters animate up when scrolled into view */}
            <div
              className={hasScrollStory
                ? "mt-14 grid grid-cols-2 gap-6 max-w-md mx-auto"
                : "mt-14 grid grid-cols-2 gap-6 max-w-md"
              }
            >
              {stats.map((s) => (
                <AnimatedStat
                  key={s.label}
                  statConfig={s}
                  live={s.key === "exchange_count" ? 20 : (counts[s.key] || 0)}
                />
              ))}
            </div>
            </div>{/* /kr-console-body */}
          </div>{/* /kr-console */}
        </div>{/* /max-w-1200 */}
      </section>{/* /hero-payoff */}

      {/* v49 — "Trusted by" band (client logos). */}
      <TrustedByBand />

      {/* Console → cards: v36 subtle vertical dotted divider — same
          visual language as the drop-line above the console. Static
          (no animation) so it stays quiet between sections. */}
      <DottedDivider testid="payoff-cards-divider" extraStyle={{ marginTop: "2vh", marginBottom: "1vh" }} />

      {/* Core features — bento layout: wider Creator Intel + Exchange
          Intel; full-width Prospect Radar. Cards use kr-card2 (mouse-
          spotlight + hover glow, no always-on halo). Section no
          longer carries a top hairline border — the heartbeat divider
          above IS the section boundary, and the extra hairline was
          reading as a faint white line under the divider. */}
      <section ref={cardsSectionRef} className="relative z-10 kr-reveal" data-testid="core-features-section">
        <div className="max-w-[1240px] mx-auto px-6 py-28">
          <div className="text-center">
            <div className="mono text-[11px] uppercase tracking-[0.25em] kr-eyebrow-breathe" style={{ color: "var(--primary)" }}>
              {`${MODULE_WORDS[features.length] || features.length} modules · One radar`}
            </div>
            <h2 className="display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight font-semibold">
              <WordHeading text="Find New Creators, Daily!" colored={["Daily!"]} />
            </h2>
          </div>

          <div
            className="mt-16 grid gap-5"
            style={{ gridTemplateColumns: "repeat(12, minmax(0,1fr))", alignItems: "stretch" }}
            data-testid="features-bento-grid"
          >
            {features.map((f, i) => {
              // Prototype bento: row 1 (4+8), row 2 (8+4),
              // row 3 (4+4+4), row 4 (12 full — text left, tall
              // visual right). Grid `align-items: stretch` keeps
              // heights uniform per row.
              const span =
                f.span === "full" ? "col-span-12"
                : f.span === "half" ? "col-span-12 md:col-span-6"
                : f.span === "wide" ? "col-span-12 md:col-span-8"
                : "col-span-12 md:col-span-4";
              // Prospect Radar (full) uses a 2-col inner layout:
              // description on the left, tall canvas on the right.
              const isFull = f.span === "full";
              const isWide = f.span === "wide" || f.span === "half";
              return (
                <div
                  key={f.n}
                  data-testid={f.testid}
                  onClick={() => nav(f.to)}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx((prev) => (prev === i ? -1 : prev))}
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty("--kr-mx", `${e.clientX - r.left}px`);
                    e.currentTarget.style.setProperty("--kr-my", `${e.clientY - r.top}px`);
                  }}
                  className={`kr-card2 group p-6 cursor-pointer ${isFull ? "" : "flex flex-col"} ${span}`}
                  style={{ ["--kr-i"]: i }}
                >
                  {isFull ? (
                    <div className={f.visual === "botflag" || f.visual === "livepulse" ? "grid gap-6 grid-cols-1 sm:grid-cols-2" : "grid gap-6"}
                      style={f.visual === "botflag" || f.visual === "livepulse" ? undefined : { gridTemplateColumns: "1fr 1fr" }}>
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between">
                          <div className="w-11 h-11 rounded-md flex items-center justify-center border hairline" style={{ background: "rgba(0,240,255,0.08)" }}>
                            <f.icon size={20} style={{ color: f.tone }} strokeWidth={1.6} />
                          </div>
                          <div className="flex items-center gap-2">
                            {f.isNew && (
                              <span data-testid={`${f.testid}-new-badge`}
                                className="mono text-[9px] px-1.5 py-0.5 rounded uppercase tracking-[0.2em]"
                                style={{ background: "rgba(0,240,255,0.14)", color: "var(--primary)", border: "1px solid rgba(0,240,255,0.35)" }}>New</span>
                            )}
                            <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">{f.n}</span>
                          </div>
                        </div>
                        <div className="display text-xl font-semibold mt-5">{f.title}</div>
                        <p className="text-sm text-zinc-400 leading-relaxed mt-3">{f.body}</p>
                        <div className="mt-6 flex items-center gap-1 text-xs mono uppercase tracking-[0.2em] group-hover:translate-x-0.5 transition-transform duration-150"
                          style={{ color: f.tone }}>
                          {f.cta} <ArrowRight size={12} />
                        </div>
                      </div>
                      <div className="kr-card-vis kr-card-vis-tall" data-testid={`${f.testid}-visual`}>
                        <ModuleCanvas kind={f.visual} cardIdx={i} bootStartAt={bootStartAt} hovered={hoveredIdx === i} tall />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="w-11 h-11 rounded-md flex items-center justify-center border hairline" style={{ background: "rgba(0,240,255,0.08)" }}>
                          <f.icon size={20} style={{ color: f.tone }} strokeWidth={1.6} />
                        </div>
                        <div className="flex items-center gap-2">
                          {f.isNew && (
                            <span data-testid={`${f.testid}-new-badge`}
                              className="mono text-[9px] px-1.5 py-0.5 rounded uppercase tracking-[0.2em]"
                              style={{ background: "rgba(0,240,255,0.14)", color: "var(--primary)", border: "1px solid rgba(0,240,255,0.35)" }}>New</span>
                          )}
                          <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">{f.n}</span>
                        </div>
                      </div>
                      <div className="display text-lg font-semibold mt-5">{f.title}</div>
                      <p className="text-sm text-zinc-400 leading-relaxed mt-2" style={f.span === "half" ? { marginBottom: 18 } : undefined}>{f.body}</p>
                      <div className={`kr-card-vis ${isWide ? "kr-card-vis-wide" : ""} mt-auto`} data-testid={`${f.testid}-visual`}
                        style={f.span === "half" ? { marginTop: "auto", height: 160 } : undefined}>
                        <ModuleCanvas kind={f.visual} cardIdx={i} bootStartAt={bootStartAt} hovered={hoveredIdx === i} wide={isWide} />
                      </div>
                      <div className="mt-5 flex items-center gap-1 text-xs mono uppercase tracking-[0.2em] group-hover:translate-x-0.5 transition-transform duration-150"
                        style={{ color: f.tone }}>
                        {f.cta} <ArrowRight size={12} />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cards → finale: no divider. The rings section follows the
          cards directly (prototype). */}

      {/* CTA strip — the finale. Prototype layout: centred stack over
          a full-canvas dot field. Rings roll out from the Enter
          Terminal button (target for FinaleCanvas). Above the
          eyebrow, a typed "> radar ready" terminal line with a
          blinking block cursor. Heading lights up word-by-word. */}
      <section
        ref={finaleSectionRef}
        className="relative z-10 overflow-hidden kr-reveal"
        style={{ background: "rgba(10,10,11,0.6)" }}
        data-testid="finale-section"
      >
        <FinaleCanvas targetRef={enterTerminalRef} />
        <div className="max-w-[1000px] mx-auto px-6 py-24 relative z-10 text-center flex flex-col items-center gap-6">
          <div className="mono text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--primary)" }}>
            <TypedTerminal
              text="> radar ready"
              testid="finale-term-line"
              sectionRef={finaleSectionRef}
            />
          </div>
          <div className="display text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight max-w-3xl">
            <WordHeading text="Map the market. Onboard the right creators." colored={["Onboard", "the", "right", "creators."]} />
          </div>
          <div className="text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
            Discover, Compare &amp; Monitor Thousands of Traders and KOLs in real-time within seconds.
          </div>
          <div className="mt-4" data-testid="finale-cta-wrap">
            <button
              ref={enterTerminalRef}
              data-testid="footer-cta-search"
              onClick={() => nav("/search")}
              className="kr-cta-primary relative inline-flex items-center gap-3 rounded-md font-semibold text-base"
              style={{
                background: "var(--primary)",
                color: "#0A0A0B",
                height: "52px",
                padding: "0 28px",
              }}
            >
              Enter Terminal <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t hairline backdrop-blur-sm" data-testid="site-footer" style={{ background: "rgba(10,10,11,0.65)" }}>
        <div className="max-w-[1200px] mx-auto px-6 py-16">
          <div className="grid gap-10 md:grid-cols-5">
            {/* Brand column */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "var(--primary)" }}>
                  <Radar size={16} strokeWidth={2} color="#0A0A0B" />
                </div>
                <span className="display text-[15px] font-semibold tracking-tight">
                  KOL <span style={{ color: "var(--primary)" }}>Radar</span>
                </span>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed mt-4">
                Multi-market KOL intelligence for exchanges, brokers and growth teams.
              </p>
              <div className="mt-5 flex items-center gap-3" data-testid="footer-socials">
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="w-8 h-8 rounded-md border hairline flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/30 transition-colors duration-150"
                    aria-label={s.label}
                    title={s.label}
                  >
                    <s.icon size={14} strokeWidth={1.6} />
                  </a>
                ))}
              </div>
            </div>

            {/* Link columns */}
            {FOOTER_COLUMNS.map((col) => (
              <div key={col.heading}>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">{col.heading}</div>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.to.startsWith("/") ? (
                        <Link
                          to={l.to}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="text-sm text-zinc-400 hover:text-white transition-colors duration-150"
                        >
                          {l.label}
                        </Link>
                      ) : (
                        <a
                          href={l.to}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="text-sm text-zinc-400 hover:text-white transition-colors duration-150"
                        >
                          {l.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-6 border-t hairline flex flex-wrap items-center justify-between gap-4">
            <div className="mono text-[11px] text-zinc-500">
              © {new Date().getFullYear()} KOL Radar · Data intelligence platform.
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Mail size={12} /> <a href="mailto:hello@kolradar.io" data-testid="footer-contact-email" className="hover:text-white transition-colors duration-150">hello@kolradar.io</a>
            </div>
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <Link to="/privacy" className="hover:text-white transition-colors duration-150" data-testid="footer-bottom-privacy">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors duration-150" data-testid="footer-bottom-terms">Terms</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors duration-150" data-testid="footer-bottom-disclaimer">Disclaimer</Link>
              <AdminFooterLink />
            </div>
          </div>
          <div className="mt-3 mono text-[10px] text-zinc-600 leading-relaxed" data-testid="footer-data-note">
            All metrics, exchange mappings, gap matrices, and alerts are derived from publicly
            available data and represent algorithmic best-guess estimates. No insider information
            is used. Third-party marks are the property of their respective owners.
          </div>
        </div>
      </footer>
    </div>
  );
}
